import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, HourglassEmpty, Refresh, Send } from '@mui/icons-material';
import styles from './MultipleOtpComponent.module.css';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { globalNotifyError, globalNotifySuccess, globalNotifyWarning } from 'views/utilities/AlertToast';
import { checkDmtStatusAPI, getSplitReceiptApi } from 'utils/API/Merchant APIs/DmtApiCalls';
import Spinner from 'views/Components/LoadingSinner';
import { useNavigate } from 'react-router';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchWalletBalanceAsync } from 'redux/WalletSlice';
import { useDispatch } from 'react-redux';
const MultipleOtpComponent = ({
    open,
    onClose,
    totalAmount,
    otpLength = 6,
    sendOtp,
    transferSubmit,
    dmt_type,
    serviceCharge,
    recieptData,
    singleOtp = false
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otps, setOtps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [successSteps, setSuccessSteps] = useState([]);
    const [initiatedSteps, setInitiatedSteps] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [splits, setSplits] = useState([]);
    const [transactionStatus, setTransactionStatus] = useState([]);
    const [allCompleted, setAllCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState({ sendingOtp: false, verifyingOtp: false, statusChecking: false, fetchingReceipt: false });
    const [splitId, setSplitId] = useState(null);
    // const [transactionDetails, setTransactionDetails] = useState({ receipt: null, transactions: [] });
    const agentid = localStorage.getItem('id');

    const handleChange = (index, value) => {
        const updated = [...otps];
        updated[index] = value;
        setOtps(updated);
    };
    const handleResendOtp = (index) => {
        handleSendOtp(index);
    };
    const updateTransactionStatus = (index, status) => {
        setTransactionStatus((prev) => {
            const updated = [...prev];
            updated[index] = status; // ✅ status is "SUCCESS" | "FAILED" | "INITIATED"
            return updated;
        });
    };

    const calculateTotalSteps = () => {
        if (singleOtp) return 1;
        const steps = Math.ceil(totalAmount / 5000);
        return steps > 5 ? 5 : steps;
    };
    const splitAmount = (amount) => {
        if (singleOtp) {
            setSplits([amount]);
            return;
        }
        const result = [];
        let remaining = amount;

        while (remaining > 0) {
            if (remaining >= 5000) {
                result.push(5000);
                remaining -= 5000;
            } else {
                result.push(remaining);
                remaining = 0;
            }
        }
        setSplits(result);
    };
    const handleReceiptClick = async () => {
        let receiptData;
        if (splitId) {
            receiptData = await getAllTransactionReceipt();
        }
        if (splits.length > 1) {
            navigate('/dmt/multiplereciept', { state: { receiptData: receiptData, dmtType: dmt_type } });
        } else {
            navigate('/dmt/dmtreciept', { state: recieptData });
        }
    };
    const getAllTransactionReceipt = async () => {
        setIsLoading((prev) => ({ ...prev, fetchingReceipt: true }));
        const agentid = localStorage.getItem('id');
        const response = await getSplitReceiptApi({ unique_sid: splitId, agentid });
        if (response?.status === 200) {
            // const transactionDetails = response?.data?.transactions;
            const transactionReceipt = response?.data?.receipt;
            // setTransactionDetails({ transactions: transactionDetails, receipt: transactionReceipt });
            setIsLoading((prev) => ({ ...prev, fetchingReceipt: false }));
            return transactionReceipt;
        }
        setIsLoading((prev) => ({ ...prev, fetchingReceipt: false }));
    };
    const handleStatusCheck = async (index, responseData = null, retryCount = 0) => {
        setIsLoading((prev) => ({ ...prev, statusChecking: true }));
        const refnumber = initiatedSteps[index]?.refnumber || responseData?.refnumber;
        const date = initiatedSteps[index]?.date || responseData?.date;
        const isAutoCheck = responseData ? true : false;

        try {
            const payload = { dmt_type, agentid, refnumber, date, isAutoCheck };
            const Response = await checkDmtStatusAPI(payload);

            if (Response?.status === 200) {
                const status = Response?.data?.data?.status;
                dispatch(fetchWalletBalanceAsync());
                if (isAutoCheck) {
                    handleSendOtp(index + 1);
                    setActiveStep(index + 1);
                }
                setIsLoading((prev) => ({ ...prev, statusChecking: false }));
                if (status === 'FAILED') {
                    globalNotifyError('Transaction failed. Please try again.');
                    updateTransactionStatus(index, 'FAILED');
                } else if (status === 'PENDING') {
                    globalNotifyWarning('Transaction is still pending. Please wait.');
                    updateTransactionStatus(index, 'INITIATED');
                } else if (status === 'SUCCESS' || status === 'Success') {
                    globalNotifySuccess('Transaction completed successfully!');
                    updateTransactionStatus(index, 'SUCCESS');
                } else {
                    globalNotifyError(Response?.data?.message);
                    updateTransactionStatus(index, 'FAILED');
                }
            } else if (Response?.status === 201 && Response?.data?.status === 'INITIATED') {
                // Retry logic with max 3 retries
                if (retryCount < 2) {
                    const delay = 3000;
                    setTimeout(() => {
                        handleStatusCheck(index, responseData, retryCount + 1);
                    }, delay);
                } else {
                    handleSendOtp(index + 1);
                    setActiveStep(index + 1);
                    setIsLoading((prev) => ({ ...prev, statusChecking: false }));
                    console.warn('Max retries reached. Stopping recursive checks.');
                    globalNotifyWarning('Transaction status still pending. Please check later.');
                }
            }
        } catch (e) {
            console.error('Status check failed:', e.message);
        }
    };

    const handleSendOtp = async (index) => {
        setIsLoading((prev) => ({ ...prev, sendingOtp: true }));
        const is_split = splits?.length > 1 ? 1 : 0;
        const response = await sendOtp(splits[index], is_split);
        if (response) {
            globalNotifySuccess(`OTP has been delivered for amount ₹${splits[index]}`);
        }
        setIsLoading((prev) => ({ ...prev, sendingOtp: false }));
    };
    const handleSubmit = async (index) => {
        setIsSubmitting(true);
        const split_status = splits?.length > 1 ? true : false;
        const response = await transferSubmit({ otp: otps[index], amount: splits[index], split_status, splitId });
        const splitUniqueId = response?.uniqueSid;
        dispatch(fetchWalletBalanceAsync());
        setSplitId(splitUniqueId);
        if (!response?.isSuccess) {
            globalNotifyError('OTP verification failed. Please try again.');
            setIsSubmitting(false);
            return;
        } else if (response?.status === 'PENDING') {
            updateTransactionStatus(index, 'INITIATED');
            setInitiatedSteps((prev) => {
                const updated = [...prev, { ...response, index }];
                if (updated.length === otps.length) {
                    // if (splitUniqueId) {
                    //     getAllTransactionReceipt(splitUniqueId);
                    // }
                    setAllCompleted(true);
                    globalNotifySuccess('All steps completed successfully!');
                } else {
                    // handleSendOtp(index + 1);
                    setIsLoading((prev) => ({ ...prev, statusChecking: true }));
                    setTimeout(() => {
                        handleCheckStatus(index, { ...response, index });
                    }, 2000);
                }

                return updated;
            });
            // setActiveStep(index + 1);
        } else if (response?.status === 'SUCCESS' || response?.status === 'Success') {
            updateTransactionStatus(index, 'SUCCESS');
            setSuccessSteps((prev) => [...prev, index]);
            setActiveStep(index + 1);
        }
        setIsSubmitting(false);
    };
    const handleCheckStatus = (index, responseData = null) => {
        handleStatusCheck(index, responseData);
    };
    useEffect(() => {
        const totalSteps = calculateTotalSteps();
        splitAmount(totalAmount);
        setOtps(Array(totalSteps).fill(''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (splits.length > 0) {
            handleSendOtp(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splits]);
    const progress = (initiatedSteps.length / otps.length) * 100;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" scroll="paper" fullWidth PaperProps={{ className: styles.dialogPaper }}>
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <DialogTitle className={styles.title}>
                        <motion.div className={styles.titleContainer}>
                            {/* <Security className={styles.titleIcon} /> */}
                            <Typography variant="h5" className={styles.titleText}>
                                OTP Authentication
                            </Typography>
                            <Typography variant="body2" className={styles.subtitle}>
                                Verify OTP for each split transaction
                            </Typography>
                            {/* Transaction details */}
                            <Box className={styles.amountInfo}>
                                <Typography variant="body2" className={styles.amountLabel}>
                                    Transaction Amount:
                                </Typography>
                                <Typography variant="body1" className={styles.amountValue}>
                                    ₹ {totalAmount}
                                </Typography>

                                <Typography variant="body2" className={styles.amountLabel}>
                                    Service Charge:
                                </Typography>
                                <Typography variant="body1" className={styles.amountValue}>
                                    ₹ {serviceCharge}
                                </Typography>
                            </Box>
                        </motion.div>

                        <Box className={styles.progressContainer}>
                            <LinearProgress variant="determinate" value={progress} className={styles.progressBar} />
                            <Typography variant="caption" className={styles.progressText}>
                                {initiatedSteps.length} of {otps.length} completed
                            </Typography>
                        </Box>
                    </DialogTitle>

                    <DialogContent className={styles.content}>
                        {allCompleted ? (
                            <motion.div
                                className={styles.successContainer}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                            >
                                {/* Subtle background gradient */}
                                <div className={styles.backgroundGradient} />

                                {/* Success icon with refined animation */}
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                        delay: 0.2,
                                        type: 'spring',
                                        stiffness: 200,
                                        damping: 20
                                    }}
                                    className={styles.iconContainer}
                                >
                                    <div className={styles.iconWrapper}>
                                        <CheckCircle className={styles.successIcon} />
                                    </div>
                                    <motion.div
                                        className={styles.iconRing}
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1.1, opacity: 0.6 }}
                                        transition={{
                                            delay: 0.4,
                                            duration: 1.5,
                                            repeat: Infinity,
                                            repeatType: 'reverse',
                                            ease: 'easeInOut'
                                        }}
                                    />
                                </motion.div>

                                {/* Content section */}
                                <motion.div
                                    className={styles.contentSection}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4, duration: 0.5 }}
                                >
                                    <h2 className={styles.title}>Transaction Complete</h2>
                                </motion.div>

                                {/* ✅ Transaction Summary Table */}
                                <motion.div
                                    className={styles.tableWrapper}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 0.6 }}
                                >
                                    <Typography variant="h6" className={styles.tableTitle}>
                                        Transaction Summary
                                    </Typography>

                                    <table className={styles.transactionTable}>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Reference</th>
                                                <th>Status Check</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {splits.map((amt, idx) => (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td>₹{amt}</td>
                                                    <td>
                                                        <span
                                                            className={`${styles.statusBadge} ${
                                                                transactionStatus[idx] === 'SUCCESS'
                                                                    ? styles.success
                                                                    : transactionStatus[idx] === 'FAILED'
                                                                    ? styles.failed
                                                                    : styles.pending
                                                            }`}
                                                        >
                                                            {transactionStatus[idx] || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>{initiatedSteps[idx]?.refnumber || '—'}</td>
                                                    <td>
                                                        {/* ✅ Icon Button with Tooltip */}
                                                        <Tooltip title="Check Status">
                                                            <IconButton color="primary" size="small" onClick={() => handleCheckStatus(idx)}>
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </motion.div>

                                {/* Action button */}
                                <motion.button
                                    className={styles.actionButton}
                                    onClick={handleReceiptClick}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6, duration: 0.5 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <span>View Receipt</span>
                                    <ArrowRight className={styles.buttonIcon} />
                                </motion.button>

                                {/* Status indicator */}
                                <motion.div
                                    className={styles.statusIndicator}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.8, duration: 0.5 }}
                                >
                                    <div className={styles.statusDot} />
                                    <span className={styles.statusText}>Verified & Secured</span>
                                </motion.div>
                                <p className={styles.subtitle}>Transaction successful! View your receipt.</p>
                            </motion.div>
                        ) : (
                            <motion.div className={styles.otpContainer}>
                                {otps.map((otp, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -30, scale: 0.9 }}
                                        animate={{
                                            opacity: 1,
                                            x: 0,
                                            scale: 1,
                                            transition: {
                                                duration: 0.5,
                                                delay: index * 0.15,
                                                type: 'spring',
                                                stiffness: 100
                                            }
                                        }}
                                        className={`${styles.otpBox} ${index === activeStep ? styles.active : ''} ${
                                            successSteps.includes(index) ? styles.completed : ''
                                        }`}
                                    >
                                        <div className={styles.stepIndicator}>
                                            <span className={styles.stepNumber}>{index + 1}</span>
                                        </div>

                                        <div className={styles.otpContent}>
                                            {transactionStatus[index] === 'SUCCESS' ? (
                                                <motion.div
                                                    initial={{ scale: 0, rotate: -180 }}
                                                    animate={{ scale: 1, rotate: 0 }}
                                                    transition={{
                                                        type: 'spring',
                                                        stiffness: 200,
                                                        damping: 15
                                                    }}
                                                    className={styles.successWrapper}
                                                >
                                                    <CheckCircle className={styles.successIcon} />
                                                    <Typography variant="body2" className={styles.successText}>
                                                        Transaction Completed
                                                    </Typography>
                                                </motion.div>
                                            ) : initiatedSteps?.some((item) => item.index === index) ? (
                                                <motion.div
                                                    initial={{ opacity: 0, x: -50 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                    className={styles.initiatedWrapper}
                                                >
                                                    <HourglassEmpty className={styles.initiatedIcon} />
                                                    <Typography variant="body2" className={styles.initiatedText}>
                                                        Transaction Initiated
                                                    </Typography>

                                                    {/* ✅ Status Check Button */}
                                                    <Tooltip title="Check Transaction Status" arrow placement="top">
                                                        <IconButton onClick={() => handleStatusCheck(index)} className={styles.checkBtn}>
                                                            <Refresh />
                                                        </IconButton>
                                                    </Tooltip>
                                                </motion.div>
                                            ) : (
                                                <motion.div
                                                    className={styles.inputWrapper}
                                                    animate={{ scale: index === activeStep ? 1.02 : 1 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    {/* Amount text (subtle, above input) */}
                                                    <Typography variant="caption" className={styles.amountText}>
                                                        Amount to transfer: ₹ {splits[index]}
                                                    </Typography>

                                                    <TextField
                                                        label={`Authentication Code ${index + 1}`}
                                                        placeholder={`Enter ${otpLength}-digit code`}
                                                        variant="outlined"
                                                        value={otp}
                                                        inputProps={{ maxLength: otpLength }}
                                                        onChange={(e) => handleChange(index, e.target.value)}
                                                        disabled={index > activeStep}
                                                        fullWidth
                                                        className={styles.input}
                                                        InputProps={{
                                                            className: styles.inputField,
                                                            endAdornment: (
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() => handleResendOtp(index)}
                                                                    disabled={index !== activeStep || isLoading.sendingOtp}
                                                                    className={styles.resendButton}
                                                                >
                                                                    Resend
                                                                </Button>
                                                            )
                                                        }}
                                                    />

                                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                        <Button
                                                            variant="contained"
                                                            disabled={index > activeStep || otp.trim().length !== otpLength || isSubmitting}
                                                            onClick={() => handleSubmit(index)}
                                                            className={styles.submitButton}
                                                            startIcon={<Send />}
                                                        >
                                                            {isLoading.verifyingOtp && index === activeStep ? 'Verifying...' : 'Verify'}
                                                        </Button>
                                                    </motion.div>
                                                </motion.div>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </DialogContent>

                    <DialogActions className={styles.actions}>
                        <Typography variant="caption" className={styles.helpText}>
                            Check your registered mobile for OTP codes
                        </Typography>
                        <Button onClick={onClose} className={styles.closeButton} variant="outlined">
                            Close
                        </Button>
                    </DialogActions>
                </motion.div>
            </Dialog>
            <Spinner loading={isLoading.sendingOtp || isLoading.fetchingReceipt || isLoading.statusChecking} />
        </>
    );
};

export default MultipleOtpComponent;
MultipleOtpComponent.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    totalAmount: PropTypes.number,
    otpLength: PropTypes.number,
    sendOtp: PropTypes.func.isRequired,
    transferSubmit: PropTypes.func.isRequired,
    dmt_type: PropTypes.number,
    serviceCharge: PropTypes.any,
    recieptData: PropTypes.object,
    singleOtp: PropTypes.bool
};
